import { animate, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, ChangeDetectorRef, Component, DoCheck, ElementRef, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ActivityDescription, ActivityReason } from '../../mru/models/activity-description.model';
import { RecommendedComponent } from '../../recommended/recommended.component';
import { MruItemInflationService } from '../../shared/mru-item/mru-item-inflation.service';
import { FabricConstants } from '../constants/fabric.constant';
import { ResponsiveRelativeDatePipe } from '../directives/responsive-relative-date/responsive-relative-date.pipe';
import { IconFontType } from '../icon-font/icon-font-type.enum';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownAreas, KnownClickDestinations, KnownInstrumentationEvents, KnownReferralPlatform } from '../instrumentation/instrumentation.service';
import { FileType } from '../models/file-item.model';
import { ItemView } from '../models/item-view.model';
import { ApplicationUrlParam } from '../models/jsapi.model';
import { ObservableScope } from '../models/observable-scope.enum';
import { Person } from '../models/person.model';
import { DocumentQueryParams } from '../models/wac-host.model';
import { Activity } from '../mru/activity.model';
import { MruFileItem } from '../mru/mru-file-item.model';
import { DocumentService } from '../services/document.service';
import { EventService } from '../services/event.service';
import { PeopleCardService } from '../services/people-card.service';
import { LaunchAction, UrlLauncherService } from '../services/url-launcher.service';
import { WacContentService } from '../services/wac-content.service';
import { WindowService } from '../services/window.service';
import { AngularEvents } from '../shared-context/angular-events';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { LocService } from '../shared-context/loc.service';
import { HtmlEscaper } from '../utilities/html-escaper.utility';
import { ActivityDescriptionService } from './activity-description.service';

interface HtmlDescription {
    activityDescription: ActivityDescription;
    html: string | SafeHtml;
    icon: IconFontType;
}

@Component({
    selector: 'ohp-activity-description',
    templateUrl: './activity-description.component.html',
    animations: [
        trigger('fadeIn', [
            transition('inactive => active', [
                style({ opacity: 0 }),
                animate('500ms 0ms linear', style({ opacity: 1 }))
            ])
        ])
    ]
})
export class ActivityDescriptionComponent implements OnInit, OnChanges, AfterViewInit, DoCheck, OnDestroy {

    @Input() public activities: Array<Activity>;

    @Input() public lastViewedTime: Date; // for recommended, this is the activity reason timestamp

    @Input() public iconOnly: boolean;

    @Input() public showIcon: boolean = true;

    @Input() public person: Person;

    @Input() public iconCssClass: string;

    @Input() public iconSize: number;

    @Input() public activityDeepLink: string;

    @Input() public title: string; // Used for putting title in second line

    @Input() public docId: string; // Used in deep links to get correct element

    @Input() public activityDescriptionInput: ActivityDescription; // A pre-built activity description. Used by Recommended items and MRU++ with flight=mruactivity since the service returns the pre-built activity string

    @Input() public index: number;

    @Input() public fileType: FileType;

    @Input() public navId: string;

    // Determines the number of lines the activity should be distributed across
    // 1: display text and timestamp on a single line
    // 2: display text on the first line and the timestamp on the second line
    // undefined: display text and timestamp together and wrap onto a second line if necessary
    @Input() public numLines: 1 | 2;

    @Input() public mobileListItem: boolean = false;

    @Input() public teamsDeepLink: string;

    @Input() public area: string = KnownAreas.Recommended;

    @Input() public useSolidIcon?: boolean = false;

    @Input() public timestampAndTitleInSameLine?: boolean = false;

    @ViewChild('descriptionElement') public descriptionElement: ElementRef;

    public htmlDescription: HtmlDescription;
    public activityState: 'inactive' | 'active';
    public activityDeepLinkHovered: boolean = false;
    public inTeams: string;
    public secondLine: string;
    public fluentRefresh: boolean;

    private needPeopleCardUpdate: boolean;
    private shortDate: boolean;
    private queuedPeopleCardUpdate: Promise<void>;
    private eventsSubscription: Subscription;
    private activityReason: ActivityReason;
    private dateString: string;
    private static readonly activitySectionId: string = 'activity-description-div';
    private clickHandler: (event: Event) => void;

    constructor(
        private readonly activityDescriptionService: ActivityDescriptionService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly documentService: DocumentService,
        private readonly domSanitizer: DomSanitizer,
        private readonly eventService: EventService,
        private readonly instrumentationService: InstrumentationService,
        private readonly locService: LocService,
        private readonly mruItemInflationService: MruItemInflationService,
        private readonly peopleCardService: PeopleCardService,
        private readonly responsiveRelativeDatePipe: ResponsiveRelativeDatePipe,
        private readonly urlLauncherService: UrlLauncherService,
        private readonly wacContentService: WacContentService,
        private readonly windowService: WindowService
    ) {}

    public ngOnInit() {
        this.person = this.person ;
        this.activityState = this.activities ? 'active' : 'inactive';
        const shortDateWidth = FabricConstants.MediumScreenMaxWidth;
        this.iconSize = this.iconSize || 16;
        this.shortDate = this.windowService.innerWidth < shortDateWidth;
        this.inTeams = this.locService.TimeSentInTeams;
        this.fluentRefresh = this.capabilitiesService.capabilities.fluentRefresh;
        if (this.locService.supportsLocaleSpecifiers) {
            // Automatically adjust shortDate setting based on changes to available width
            this.eventsSubscription = this.eventService.events.withScope(ObservableScope.OnlyFuture).pipe(
                filter(event => event.name === AngularEvents.contentWidthChanged))
                .subscribe(event => {
                    let newWidth: number;
                    [newWidth] = event.args;
                    this.shortDate = newWidth < shortDateWidth;
                    this.updateDescription();
                });
        }
        this.updateDescription();
    }

    public ngOnChanges() {
        this.updateDescription();
    }

    public ngAfterViewInit() {
        this.softUpdatePeopleCards();
    }

    public ngDoCheck() {
        this.softUpdatePeopleCards();
    }

    public ngOnDestroy() {
        if (this.eventsSubscription) {
            this.eventsSubscription.unsubscribe();
        }

        this.removePeopleCards();
    }

    public onHover() {
        this.updatePeopleCards();
    }

    public activityHovered() {
        this.activityDeepLinkHovered = true;
    }

    public activityUnhovered() {
        this.activityDeepLinkHovered = false;
    }

    public clicked(event: Event) {
        if (!(this.activityDeepLink || this.teamsDeepLink)) {
            return;
        }
        if (event) {
            event.preventDefault();
        }
        const linkToOpen = this.activityDeepLink || this.teamsDeepLink;

        if (!this.teamsDeepLink && this.capabilitiesService.capabilities.wacHostEnabled && this.capabilitiesService.capabilities.wacHostApps.has(this.fileType)) {

            this.logClick();
            this.wacContentService.bootstrapAndRoute(
                {
                    docUrl: linkToOpen,
                    fileType: this.fileType,
                    wdOrigin: '',
                    applicationUrlParams: { [ApplicationUrlParam.Nav]: this.navId }
                },
                { [DocumentQueryParams.Nav]: this.navId }
            );
        } else {
            this.urlLauncherService.openUrl(linkToOpen, LaunchAction.RecommendedDeepLink);
            this.logClick();
        }
    }

    public getActivityElementId(): string {
        return `${ActivityDescriptionComponent.activitySectionId}-${this.docId}`;
    }

    private logClick(uiHost = false): void {
        // TODO: if we start showing activity deep links in MRU we will need to adjust some of these params
        // Currently we only show deep links in recommended so this works for now
        const params: InstrumentationParams = {};
        params[InstrumentationParam.ItemId] = this.activityDeepLink ? 'activity-description' : 'teams-deep-link';
        params[InstrumentationParam.Index] = this.index.toString();
        params[InstrumentationParam.ItemCategory] = RecommendedComponent.recommendedListId; // TODO: don't hardcode for recommended if we expand deep links to other sections
        params[InstrumentationParam.State] = ItemView.Card;
        params[InstrumentationParam.ResourceId] = this.docId;
        params[InstrumentationParam.Area] = this.area;
        params[InstrumentationParam.DocumentType] = FileType[this.fileType];
        params[InstrumentationParam.ActivityDisplayed] = this.activityReason ? ActivityReason[this.activityReason] : '';
        params[InstrumentationParam.ClickDestination] = uiHost ? KnownClickDestinations.ContentPage : KnownClickDestinations.ExternalPage;
        const referral = MruFileItem.referralFromFileType(this.fileType);
        const eventName = this.area === KnownAreas.SuggestedActions ? KnownInstrumentationEvents.SuggestedActionClick : KnownInstrumentationEvents.RecommendedClick;

        this.instrumentationService.logClickEvent(eventName, ActivityDescriptionComponent.activitySectionId, this.area, referral, KnownReferralPlatform.Web, params);
    }

    private updateDescription(): void {
        const newActivityDescription = this.activityDescriptionInput || this.activityDescriptionService.activityDescription(this.activities, this.lastViewedTime, this.shortDate, this.numLines === 2);
        let newDescription: HtmlDescription = null;
        if (newActivityDescription) {
            newDescription = this.createDescription(newActivityDescription);
        }

        if (newDescription && (!this.htmlDescription || newDescription.html.toString() !== this.htmlDescription.html.toString())) {
            this.removePeopleCards();
            this.htmlDescription = newDescription;
            this.dateString = this.getDateString(this.htmlDescription.activityDescription.timestamp);
            this.activityState = 'active';
            this.needPeopleCardUpdate = true;
        } else if (!newDescription && this.htmlDescription) {
            this.removePeopleCards();
            this.htmlDescription = null;
            this.dateString = null;
            this.activityState = 'inactive';
        }

        if (this.timestampAndTitleInSameLine) {
            this.secondLine = `${this.dateString} · ${this.title}`;
        } else {
            this.secondLine = this.title || this.dateString;
        }

        if (this.capabilitiesService.capabilities.recommendedDeepLinkEnabled || this.capabilitiesService.capabilities.recommendedTeamsLinkEnabled) {
            this.changeDetectorRef.detectChanges();
            const activityDeepLinkElement = this.documentService.nativeDocument.getElementById(this.getActivityElementId());
            if (activityDeepLinkElement) {
                if (this.clickHandler) {
                    activityDeepLinkElement.removeEventListener('click', this.clickHandler);
                }
                this.clickHandler = event => this.clicked(event);
                activityDeepLinkElement.addEventListener('click', this.clickHandler);
            }
        }
    }

    private createDescription(activityDescription: ActivityDescription): HtmlDescription {
        this.activityReason = activityDescription ? activityDescription.reasonType : undefined;
        if (!activityDescription || !activityDescription.formatString) {
            return null;
        }
        return {
            activityDescription,
            html: this.generateHtml(activityDescription),
            icon: this.reasonTypeToIcon(activityDescription.reasonType, this.useSolidIcon)
        };
    }

    private generateHtml(activityDescription: ActivityDescription): string | SafeHtml {
        if (!activityDescription.people || !activityDescription.people.length) {
            return activityDescription.formatString;
        }

        // Important: We use 'bypassSecurityTrustHtml' below so that the 'data-email' attribute we're adding to the people card element doesn't
        // get deleted by Angular's DOM sanitizer. Therefore, it's important we escape this entire string to avoid XSS attacks through the activity strings.
        let html = HtmlEscaper.escapeHtml(activityDescription.formatString);

        if (this.activityDeepLink) {
            const elementArray = html.split(/(&lt;c[0-9]+&gt;.*?&lt;\/c[0-9]+&gt;)/);
            for (let i = 0; i < elementArray.length; i++) {
                if (i % 2 === 0) {
                    elementArray[i] = ` <span class='activity-deep-link__underlined'>${elementArray[i].trim()}</span> `;
                }
            }
            html = elementArray.join('').trim();
        }

        for (let i = 0; i < activityDescription.people.length; i++) {
            const person = activityDescription.people[i];
            if (person.tag) {
                html = html.replace(new RegExp(`&lt;${person.tag.trim()}&gt;`, 'g'), person.upn ? `<span class="activity-user-name ms-fcl-nd ms-fcl-nd-h${this.fluentRefresh ? ' activity-user-name--fluent' : ''}" title="${HtmlEscaper.escapeHtml(person.name)}" data-email="${HtmlEscaper.escapeHtml(person.upn)}">` : '<span>');
                html = html.replace(new RegExp(`&lt;/${person.tag.trim()}&gt;`, 'g'), '</span>');
            }
        }

        return this.domSanitizer.bypassSecurityTrustHtml(html);
    }

    private reasonTypeToIcon(reasonType: ActivityReason, solidIcon: boolean): IconFontType {
        switch (reasonType) {
            case ActivityReason.Edit:
                return solidIcon ? IconFontType.PencilSolid : IconFontType.Pencil;
            case ActivityReason.Comment:
                return solidIcon ? IconFontType.Chat : IconFontType.Comment;
            case ActivityReason.Mention:
                return IconFontType.At;
            case ActivityReason.Open:
                return IconFontType.FolderOpen; // TODO: not sure if this is the right icon
            case ActivityReason.Share:
                return IconFontType.People2;
            case ActivityReason.Attachment:
                return IconFontType.Attach;
            case ActivityReason.SnoozeExpiry:
                return IconFontType.Calendar; // TODO: Placeholder icon for Recommended snooze
            case ActivityReason.Trending:
                return IconFontType.Trending; // TODO: Placeholder for Recommended trending
            case ActivityReason.News:
                return IconFontType.News;
            case ActivityReason.Meeting:
                return IconFontType.Calendar;
            case ActivityReason.Video:
                return IconFontType.Video;
            case ActivityReason.Task:
                return IconFontType.Task;
            case ActivityReason.Default:
                return IconFontType.Compass;
            default:
                return IconFontType.Compass;
        }
    }

    private getDateString(time: Date): string {
        const fallbackText = this.locService.localizedDateString(time);
        return this.responsiveRelativeDatePipe.transform(time, this.shortDate, fallbackText, false);
    }

    private softUpdatePeopleCards(): void {
        if (this.capabilitiesService.capabilities.peopleCardsEnabled) {
            if (this.peopleCardService.population() > 0) {
                this.updatePeopleCards();
                return;
            }
            if (this.queuedPeopleCardUpdate || !this.needPeopleCardUpdate) {
                return;
            }
            this.queuedPeopleCardUpdate = this.mruItemInflationService.waitOnInflations().then(() => {
                this.updatePeopleCards();
                this.queuedPeopleCardUpdate = null;
            });
        }
    }

    private updatePeopleCards(): void {
        if (!this.capabilitiesService.capabilities.peopleCardsEnabled || !this.needPeopleCardUpdate || !this.htmlDescription || !this.htmlDescription.activityDescription || !this.htmlDescription.activityDescription.people) {
            this.needPeopleCardUpdate = false;
            return;
        }

        // If element hasn't yet rendered, don't attempt to update people cards
        // needPeopleCardUpdate should not be set to false here as this method
        // may be executed immediately after it is set to true, but before the description renders
        if (!this.descriptionElement || !this.descriptionElement.nativeElement) {
            return;
        }

        this.changeDetectorRef.detectChanges();

        const people = this.descriptionElement.nativeElement.querySelectorAll('.activity-user-name');
        for (let i = 0; i < people.length; i++) {
            const personElem = people[i];
            const email: string = personElem.getAttribute('data-email');
            this.peopleCardService.addPersonaCard(people[i], email);
        }

        this.needPeopleCardUpdate = false;
    }

    private async removePeopleCards() {
        if (!this.capabilitiesService.capabilities.peopleCardsEnabled || !this.htmlDescription || !this.htmlDescription.activityDescription || !this.htmlDescription.activityDescription.people || !this.descriptionElement || !this.descriptionElement.nativeElement) {
            return;
        }

        const people = this.descriptionElement.nativeElement.querySelectorAll('.activity-user-name');
        for (let i = 0; i < people.length; i++) {
            const personElem = people[i];
            const email: string = personElem.getAttribute('data-email');
            try {
                await this.peopleCardService.removePersonaCard(people[i], email);
            } catch { /* Do nothing */ }
        }
    }
}
